import { ChangeEvent, MouseEvent, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import {
  ALERT_MESSAGES,
  DOCUMENT_STATUS_VERIFIED_NOT_ELIGIBLE,
  DOCUMENT_TYPE_UPLOAD_FILES_FOR_STEPS,
  DOCUMENT_UPLOAD_RETRY_LIMIT,
  FILE_STATUS,
  INVALID,
  STATUS,
  STATUS_VERIFICATION_ALLOW_UPLOAD_RETRY,
} from '../enums/common'
import {
  deleteDocument,
  getDocumentsDetails,
  uploadDocuments,
} from '../services/Kyc'
import {
  IDocInfo,
  IDocumentUserDetail,
  IFileUploadAlert,
  IUploadWithRetryFormState,
  IUploadWithRetryHookProps,
  TUpdateWithRetryFileErrors,
  TUploadAction,
} from '../interfaces/Upload'
import {
  createFileObject,
  getDocumentWithRetryStatusAlert,
  handleError,
} from '../services/utils'
import { loadingActions } from '../store/slices/Loading'
import { IStore } from '../interfaces/Store'
import { shouldDeleteDocumentsActions } from '../store/slices/DeleteDocuments'
import { shouldDisplayValidateDocumentsActions } from '../store/slices/DisplayValidateDocuments'

const useUploadWithRetryHook = (props: IUploadWithRetryHookProps) => {
  const {
    index,
    setState,
    mainState,
    validateFile,
    selectIndex = '',
    refreshDocumentsState,
  } = props
  const fileIndex = props.fileIndex || 0
  const state = mainState[index]
  const breadcrumbStep: number = useSelector(
    (storeState: IStore) => storeState.breadcrumbReducer.step
  )
  const shouldDeleteDocuments: boolean = useSelector(
    (storeState: IStore) =>
      storeState.deleteDocumentsReducer.shouldDeleteDocuments
  )
  const shouldDisplayValidateDocuments: boolean = useSelector(
    (storeState: IStore) =>
      storeState.displayValidateDocumentsReducer.shouldDisplayValidateDocuments
  )
  const step = breadcrumbStep.toString() + selectIndex

  const dispatch = useDispatch()
  const history = useHistory()
  const updateState = useCallback(
    (
      callBack: (
        indexPrevStat: IUploadWithRetryFormState
      ) => IUploadWithRetryFormState
    ) => {
      setState((prevState: IUploadWithRetryFormState[]) => {
        if (prevState[index]) {
          const indexPrevState = { ...prevState[index] }
          const newIndexState = callBack(indexPrevState)
          const newState = [...prevState]
          newState[index] = newIndexState
          return newState
        }
        return prevState
      })
    },
    [index, setState]
  )

  const getExistingFiles = useCallback(
    async (docsInfo: IDocInfo[], validateButton: boolean = true) => {
      updateState((prevState) => ({ ...prevState, status: STATUS.pending }))
      try {
        let documents = await getDocumentsDetails(docsInfo)
        let uidDocumentToDelete = null
        let validDocuments = 0

        // If the documents are not valid (status < 8) they should be deleted and new details retrieved
        documents.forEach((document) => {
          if (
            Number(document.statutVerification2) <
              DOCUMENT_STATUS_VERIFIED_NOT_ELIGIBLE &&
            shouldDeleteDocuments
          ) {
            uidDocumentToDelete = document.documentUid
          }
          if (document.status === 'valid') {
            validDocuments += 1
          }
        })

        if (documents.length === validDocuments) {
          dispatch(
            shouldDisplayValidateDocumentsActions.setShouldDisplayValidateDocuments(
              false
            )
          )
        }

        if (uidDocumentToDelete) {
          await deleteDocument({ documentUid: uidDocumentToDelete })
          const [docInfo] = docsInfo

          // Return an 'empty' document object in order to avoid creating a new API call for each one of them after deletion
          const newDocuments = documents.map(() => {
            return {
              count: '0',
              documentTypeId: docInfo.typeId.toString(),
              documentUid: '',
              personTypeId: docInfo.personTypeId.toString(),
            }
          })
          documents = newDocuments
        }
        dispatch(shouldDeleteDocumentsActions.setShouldDeleteDocuments(false))

        const validationButtonError: IFileUploadAlert | null = null
        documents.forEach((document, documentIndex) => {
          let file: File | null = null
          let fileAlerts: IFileUploadAlert | null = null
          let fileStatus = FILE_STATUS.pending
          const documentsUid = [document.documentUid]
          if (document.documentUid) {
            fileAlerts = getDocumentWithRetryStatusAlert(
              document.status,
              Number(document.documentTypeId),
              Number(document.statutVerification2),
              Number(document.count)
            )
            if (document.status !== INVALID) {
              file = createFileObject([''], document.name)
              fileStatus = FILE_STATUS.loaded
            }
          }
          updateState((prevState) => {
            const files = [...prevState.file]
            const filesStatus = [...prevState.fileStatus]
            const filesAlerts = [...prevState.fileAlerts]
            const uploadCounts = [...prevState.uploadCount]
            const statusVerifications = [...prevState.statusVerification]
            const canRetry = [...prevState.canRetry]
            files[documentIndex] = file
            filesStatus[documentIndex] = fileStatus
            filesAlerts[documentIndex] = fileAlerts
            uploadCounts[documentIndex] = Number(document.count)
            statusVerifications[documentIndex] = Number(
              document.statutVerification2
            )
            canRetry[documentIndex] =
              uploadCounts[documentIndex] > 0 &&
              uploadCounts[documentIndex] < DOCUMENT_UPLOAD_RETRY_LIMIT &&
              STATUS_VERIFICATION_ALLOW_UPLOAD_RETRY.includes(
                statusVerifications[documentIndex]
              )
            return {
              ...prevState,
              file: files,
              fileAlerts: filesAlerts,
              status: STATUS.loaded,
              fileStatus: filesStatus,
              docInfo: docsInfo,
              validationButtonError,
              toggleValidateButton:
                document.status === INVALID ? true : validateButton,
              canRetry,
              uploadCount: uploadCounts,
              docUserDetails: document.data as IDocumentUserDetail,
              statusVerification: statusVerifications,
              documentsUid,
            }
          })
        })
      } catch (e) {
        updateState((prevState) => ({ ...prevState, status: STATUS.loaded }))
        handleError(e, history)
      }
    },
    [updateState, shouldDeleteDocuments, shouldDisplayValidateDocuments]
  )

  const handleChange = async (
    event: ChangeEvent<HTMLSelectElement>,
    showValidateButton: boolean = true
  ) => {
    updateState((prevState) => {
      const file = [...prevState.file]
      if (prevState.fileStatus[fileIndex] === FILE_STATUS.pending) {
        file[fileIndex] = null
      }
      return { ...prevState, fileAlerts: [], file }
    })
    const { value } = event.target
    if (!showValidateButton || value === '') {
      updateState((prevState) => ({
        ...prevState,
        toggleValidateButton: false,
      }))
      return
    }
    if (value !== undefined) {
      await getExistingFiles(
        DOCUMENT_TYPE_UPLOAD_FILES_FOR_STEPS[step][value],
        showValidateButton
      )
    }
  }

  const updateFiles: TUploadAction = (file) => {
    updateState((prevState) => {
      const files = [...prevState.file]
      const filesStatus = [...prevState.fileStatus]
      const filesAlerts = [...prevState.fileAlerts]
      let { toggleValidateButton, status } = prevState
      files[fileIndex] = file
      filesStatus[fileIndex] = FILE_STATUS.pending
      filesAlerts[fileIndex] = null
      if (file === null) {
        toggleValidateButton = true
        status = STATUS.idle
      }
      return {
        ...prevState,
        file: files,
        fileStatus: filesStatus,
        fileAlerts: filesAlerts,
        toggleValidateButton,
        status,
      }
    })
  }

  const removeFiles = () => {
    updateState((prevState) => {
      return {
        ...prevState,
        file: prevState.file.map(() => null),
        fileStatus: prevState.fileStatus.map(() => FILE_STATUS.pending),
        fileAlerts: prevState.fileAlerts.map(() => ALERT_MESSAGES.mandatory),
        toggleValidateButton: true,
        canRetry: [false, false],
        status: STATUS.idle,
      }
    })
  }

  const updateFileErrors: TUpdateWithRetryFileErrors = (error) => {
    updateState((prevState) => {
      const errors = [...prevState.fileAlerts]
      errors[fileIndex] = error
      return { ...prevState, fileAlerts: errors }
    })
  }

  const isAlreadyValidated = () => {
    if (
      state.fileStatus[fileIndex] === FILE_STATUS.loaded &&
      state.docInfo.length === 1
    ) {
      updateState((prevState) => ({ ...prevState, status: STATUS.validated }))
      return false
    }
    if (
      state.fileStatus.every((fileStatus) => fileStatus === FILE_STATUS.loaded)
    ) {
      updateState((prevState) => ({ ...prevState, status: STATUS.validated }))
      return false
    }
    return true
  }

  const uploadFile = async (
    event: MouseEvent<HTMLButtonElement>,
    identityFiles: boolean = false
  ) => {
    if (!validateFile(index)) {
      return
    }
    updateState((prevState) => ({ ...prevState, toggleValidateButton: false }))

    if (!isAlreadyValidated()) {
      return
    }

    try {
      dispatch(loadingActions.loading())
      await uploadDocuments(
        breadcrumbStep,
        state.file as File[],
        DOCUMENT_TYPE_UPLOAD_FILES_FOR_STEPS[step][state.docValue],
        identityFiles
      )
      await getExistingFiles(
        DOCUMENT_TYPE_UPLOAD_FILES_FOR_STEPS[step][state.docValue],
        false
      )
      updateState((prevState) => ({ ...prevState, status: STATUS.validated }))
      dispatch(loadingActions.loaded())
    } catch (e) {
      updateState((prevState) => {
        const filesAlerts = [...prevState.fileAlerts]
        filesAlerts[fileIndex] = ALERT_MESSAGES.fileUpload
        const status = STATUS.failed

        return {
          ...prevState,
          fileAlerts: filesAlerts,
          status,
          toggleValidateButton: true,
          file: [null, null],
        }
      })
      dispatch(loadingActions.loaded())
      handleError(e, history)
    }
  }

  const resetIndexFileState = () => {
    updateState((prevState) => ({
      ...prevState,
      file: [],
      status: STATUS.idle,
      toggleValidateButton: false,
      fileAlerts: [],
      fileStatus: [],
      validationButtonError: null,
    }))
  }

  useEffect(() => {
    if (refreshDocumentsState) {
      removeFiles()
    }
  }, [refreshDocumentsState])

  return {
    handleChange,
    getExistingFiles,
    uploadFile,
    updateFileErrors,
    updateFiles,
    removeFiles,
    resetIndexFileState,
    fIndex: fileIndex,
    shouldDisplayValidateDocuments,
  }
}

export default useUploadWithRetryHook
